import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence } from 'motion/react';
import { RefreshCcw, ArrowDown } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const controls = useAnimation();

  const PULL_THRESHOLD = 80;
  const MAX_PULL = 120;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      startY.current = e.touches[0].pageY;
      setPulling(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!pulling || refreshing) return;

    const currentY = e.touches[0].pageY;
    const diff = currentY - startY.current;

    if (diff > 0 && containerRef.current && containerRef.current.scrollTop === 0) {
      // Resistance factor
      const distance = Math.min(diff * 0.4, MAX_PULL);
      setPullDistance(distance);
      
      // Prevent browser default pull-to-refresh if possible
      if (diff > 10) {
         // This is tricky in some browsers but we try
      }
    } else {
      setPulling(false);
      setPullDistance(0);
    }
  };

  const handleTouchEnd = async () => {
    if (!pulling || refreshing) return;

    const currentDistance = pullDistance;
    setPulling(false);
    setPullDistance(0);

    if (currentDistance >= PULL_THRESHOLD) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch (e) {
        console.error("Pull to refresh failed", e);
      } finally {
        setRefreshing(false);
      }
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative h-full w-full overflow-y-auto overflow-x-hidden no-scrollbar"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <AnimatePresence>
        {(pullDistance > 0 || refreshing) && (
          <motion.div 
            initial={{ opacity: 0, y: -40 }}
            animate={{ 
              opacity: 1, 
              y: refreshing ? 20 : pullDistance - 40,
              rotate: refreshing ? 360 : (pullDistance / PULL_THRESHOLD) * 180
            }}
            exit={{ opacity: 0, y: -40 }}
            transition={refreshing ? { repeat: Infinity, duration: 1, ease: "linear" } : { type: 'spring', damping: 20 }}
            className="absolute left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          >
            <div className="bg-brand text-white p-2 rounded-full shadow-lg border-2 border-white dark:border-slate-800">
              {refreshing ? (
                <RefreshCcw className="w-5 h-5 animate-spin" />
              ) : (
                <ArrowDown className={`w-5 h-5 transition-transform ${pullDistance >= PULL_THRESHOLD ? 'rotate-180' : ''}`} />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        animate={{ y: refreshing ? 60 : pullDistance }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
    </div>
  );
}